import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Phone, Github, Calendar, Clock, GraduationCap, Code } from 'lucide-react';
import Balatro from './Balatro';
import './Login.css';

const Login = ({ onAccess }) => {
  const [userInput, setAccessCode] = useState('');
  const [dateTime, setDateTime] = useState({ date: '', time: '' });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);
  const inputRef = useRef(null);

  const subtitles = [
    'Enter access code',
    'Code provided on my resume',
    'Contact for code',
    'Welcome back!'
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    const validAccessCode = process.env.REACT_APP_ACCESS_CODE;
    const trimmedInput = userInput.trim();
    if (trimmedInput === validAccessCode) {
      onAccess(true);
    } else {
      setShowErrorModal(true);
      setAccessCode('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setDateTime({
        date: now.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        }),
        time: now.toLocaleTimeString()
      });
    };
    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSubtitleIndex(prev => (prev + 1) % subtitles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [subtitles.length]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const ErrorModal = () => (
    <div className="error-modal-overlay" role="dialog" aria-modal="true">
      <div className="error-modal">
        <div className="modal-header">
          <div className="error-icon-container">
            <X className="error-icon" aria-hidden="true" />
            <h3>Access Denied</h3>
          </div>
          <button 
            onClick={() => setShowErrorModal(false)} 
            aria-label="Close error modal"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X className="close-icon" aria-hidden="true" />
          </button>
        </div>
        <p className="error-message">
          Invalid access code. Please check your resume or contact me for the correct code.
        </p>
        <div className="modal-actions">
          <button onClick={() => setShowErrorModal(false)}>Try Again</button>
          <a href="mailto:asinalo87@gmail.com">Get Code</a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="login-container">
      {/* Balatro Background */}
      <div className="balatro-wrapper">
        <Balatro
          isRotate={false}
          mouseInteraction={true}
          pixelFilter={745}
          color1="#6c6c6cff"
          color2="#000000ff"
          color3="#000000"
        />
      </div>
      
      {/* Left Section: Login Form */}
      <div className="login-form-section">
        <div className="login-form-container">
          <div className="form-header">
            <div className="logo-container">
              <img src="/images/logo2.png" alt="Logo" className="logo" />
            </div>
            <p className="form-eyebrow">Protected Portfolio</p>
            <h1 className="form-title">Portfolio Access</h1>
            <div className="form-subtitle" aria-live="polite">
              {subtitles[currentSubtitleIndex]}
            </div>
          </div>

          <form onSubmit={handleLogin} className="login-form" noValidate>
            <div className="input-group">
              <label htmlFor="access-code">Access Code</label>
              <input
                type="password"
                id="access-code"
                ref={inputRef}
                placeholder="••••••••"
                value={userInput}
                onChange={(e) => setAccessCode(e.target.value)}
                disabled={isLoading}
                aria-required="true"
                aria-describedby="access-code-help"
              />
              <p id="access-code-help" className="sr-only">
                The access code can be found on your resume
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !userInput.trim()}
              className="submit-button"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <div className="spinner" aria-label="Loading"></div>
              ) : (
                'Access Portfolio →'
              )}
            </button>
          </form>

          <div className="form-footer">
            <p className="help-text">Need the access code?</p>
            <div className="contact-options">
              <a href="mailto:asinalo87@gmail.com" className="contact-link" aria-label="Email me">
                <Mail className="contact-icon" aria-hidden="true" />
                <span>Email</span>
              </a>
              <a href="tel:0730819763" className="contact-link" aria-label="Call me">
                <Phone className="contact-icon" aria-hidden="true" />
                <span>Call</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section: Profile Card */}
      <div className="profile-card-section">
        <div className="profile-card">
          <div className="profile-badge">
            <div className="profile-logo-container">
              <Github className="profile-logo" aria-hidden="true" />
            </div>
            <h1 className="profile-name">Sinalo Alizwa Ntlanganiso</h1>
            <p className="profile-title">Aspiring Networking-Software Engineer</p>
          </div>

          <div className="profile-details">
            <div className="detail-item">
              <Calendar className="detail-icon" aria-hidden="true" />
              <div>
                <p className="detail-label">Date</p>
                <p className="detail-value" aria-live="polite">{dateTime.date}</p>
              </div>
            </div>

            <div className="detail-item">
              <Clock className="detail-icon" aria-hidden="true" />
              <div>
                <p className="detail-label">Time</p>
                <p className="detail-value" aria-live="polite">{dateTime.time}</p>
              </div>
            </div>

            <div className="detail-item">
              <GraduationCap className="detail-icon" aria-hidden="true" />
              <div>
                <p className="detail-label">Education</p>
                <p className="detail-value">Computer Science — Graduating October 2026</p>
              </div>
            </div>
          </div>

          <a
            href="https://github.com/SA-Ntlanganiso"
            target="_blank"
            rel="noopener noreferrer"
            className="github-button"
            aria-label="View my GitHub profile"
          >
            <Github className="github-icon" aria-hidden="true" />
            <span>View GitHub Profile</span>
          </a>
        </div>
      </div>

      {showErrorModal && <ErrorModal />}
    </div>
  );
};

export default Login;